import type { StatusTone } from '@repletoryreach/sdk-ui/data-display';

/**
 * Flow-run status, as the admin projection now sends it.
 *
 * The service joins the FlowRunStatuses lookup and puts the readable code on
 * the wire, so the console compares plain strings. `status_id` travels on the
 * same DTO and is a join key: it is never rendered and never filtered on.
 *
 * The list page and the run detail page used to carry their own copies of
 * three regexes over the raw foreign key, which agreed with each other only by
 * accident and matched nothing at all in practice. One module now decides the
 * tone and the wording for every badge in this folder.
 */
export const FLOW_RUN_STATUSES = ['running', 'completed', 'failed', 'handed_off'] as const;
export type FlowRunStatus = (typeof FLOW_RUN_STATUSES)[number];

const TONES: Record<string, StatusTone> = {
  running: 'pending',
  completed: 'healthy',
  failed: 'down',
  handed_off: 'warning'
};

const LABEL_KEYS: Record<string, string> = {
  running: 'status.running',
  completed: 'status.completed',
  failed: 'status.failed',
  handed_off: 'flows.status.handedOff'
};

function normalise(status: string | null | undefined): string {
  return (status ?? '').trim().toLowerCase();
}

export function statusTone(status: string | null | undefined): StatusTone {
  return TONES[normalise(status)] ?? 'inactive';
}

/**
 * The catalogue key for a code we know. Null when the service grows a status
 * this build has not seen, in which case the caller shows the code itself
 * rather than inventing a label for it.
 */
export function statusLabelKey(status: string | null | undefined): string | null {
  return LABEL_KEYS[normalise(status)] ?? null;
}

export function isStatus(status: string | null | undefined, want: FlowRunStatus): boolean {
  return normalise(status) === want;
}

/**
 * A run counts as in flight when the service still calls it running, or when
 * it has no finish time. The second half catches a run whose worker died
 * before it could write a terminal status.
 */
export function isInFlight(status: string | null | undefined, finishedAt: string | null | undefined): boolean {
  return isStatus(status, 'running') || !finishedAt;
}
